/**
 * Score calculation utilities — ported from the backend's score.util.ts and shuffle.util.ts.
 * All scoring logic runs entirely on the frontend.
 */

/**
 * Check whether the current pieces state is the solved position.
 * Solved: pieces[i] === i for i in [0..n-2], pieces[n-1] === -1.
 */
export function isSolved(pieces: number[]): boolean {
  const n = pieces.length;
  for (let i = 0; i < n - 1; i++) {
    if (pieces[i] !== i) return false;
  }
  return pieces[n - 1] === -1;
}

/**
 * Count how many pieces are in their correct slot (including the blank at N-1).
 */
export function countCorrectPieces(pieces: number[]): number {
  const n = pieces.length;
  let correct = 0;
  for (let i = 0; i < n - 1; i++) {
    if (pieces[i] === i) correct++;
  }
  if (pieces[n - 1] === -1) correct++;
  return correct;
}

/**
 * Calculate round score.
 *
 * If the puzzle was fully solved, award maxScore.
 * Otherwise, award 0 (partial scores not implemented — matches backend).
 */
export function calculateRoundScore(
  completed: boolean,
  _correctPieces: number,
  _totalPieces: number,
  maxScore: number,
): number {
  if (completed) return maxScore;
  return 0;
}

/**
 * Calculate time bonus.
 *
 * Linear bonus based on remaining time as a fraction of the total time limit.
 * Matches the backend formula exactly.
 */
export function calculateTimeBonus(
  timeRemainingMs: number,
  timeLimitSeconds: number,
  maxTimeBonus: number,
): number {
  if (timeLimitSeconds <= 0) return 0;
  const timeRemainingSeconds = timeRemainingMs / 1_000;
  return Math.floor(
    (maxTimeBonus * Math.max(0, timeRemainingSeconds)) / timeLimitSeconds,
  );
}

/**
 * Calculate total score (round score + time bonus) in one call.
 */
export function calculateScore(opts: {
  completed: boolean;
  correctPieces: number;
  totalPieces: number;
  timeTakenMs: number;
  timeLimitSeconds: number;
  maxScore: number;
  maxTimeBonus: number;
}): number {
  const {
    completed,
    correctPieces,
    totalPieces,
    timeTakenMs,
    timeLimitSeconds,
    maxScore,
    maxTimeBonus,
  } = opts;
  const timeRemainingMs = Math.max(0, timeLimitSeconds * 1_000 - timeTakenMs);
  const roundScore = calculateRoundScore(
    completed,
    correctPieces,
    totalPieces,
    maxScore,
  );
  const bonus = completed
    ? calculateTimeBonus(timeRemainingMs, timeLimitSeconds, maxTimeBonus)
    : 0;
  return roundScore + bonus;
}
